import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCookieConsent } from "@/context/CookieConsentContext";
import { consentStateLabel } from "@/lib/cookieConsent";
import { lockPageScroll } from "@/lib/lockPageScroll";
import { CookieTable } from "./CookieTable";

export default function CookiePreferencesModal() {
  const { t } = useLanguage();
  const c = t.cookieConsent;
  const decl = t.cookieDeclaration;
  const scrollRef = useRef(null);
  const {
    settingsOpen,
    setSettingsOpen,
    draftCategories,
    setDraftCategories,
    savePreferences,
    acceptAll,
    denyAll,
  } = useCookieConsent();

  useEffect(() => {
    if (!settingsOpen) return undefined;
    return lockPageScroll();
  }, [settingsOpen]);

  if (!settingsOpen) return null;

  const tableLabels = {
    col_name: c.col_name,
    col_provider: c.col_provider,
    col_purpose: c.col_purpose,
    col_duration: c.col_duration,
    col_type: c.col_type,
    no_cookies: c.no_cookies,
  };

  return (
    <div className="fixed inset-0 z-[210] overflow-hidden" aria-modal="true">
      <button
        type="button"
        className="absolute inset-0 bg-[#3e2723]/40 backdrop-blur-sm"
        aria-label={c.close}
        onClick={() => setSettingsOpen(false)}
      />
      <div className="pointer-events-none absolute inset-0 flex items-end justify-center p-4 sm:items-center">
        <div
          className="pointer-events-auto flex max-h-[min(90vh,720px)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          role="dialog"
          aria-labelledby="cookie-modal-title"
          data-testid="cookie-preferences-modal"
        >
          <div className="flex shrink-0 items-center justify-between border-b border-[#3e2723]/10 px-5 py-4 sm:px-6">
            <h2 id="cookie-modal-title" className="font-display text-lg font-bold text-[#3e2723]">
              {c.modal_title}
            </h2>
            <button
              type="button"
              onClick={() => setSettingsOpen(false)}
              className="rounded-full p-2 text-[#3e2723]/60 hover:bg-[#faf8f8]"
              aria-label={c.close}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div
            ref={scrollRef}
            data-lenis-prevent
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6 [-webkit-overflow-scrolling:touch]"
          >
          <p className="text-sm leading-relaxed text-[#5c4039]">{c.modal_intro}</p>

          <div className="mt-6 space-y-5">
            {(decl.categories || []).map((cat) => {
              const required = cat.id === "necessary";
              const checked = required || Boolean(draftCategories[cat.id]);
              const cookies = (decl.cookies || []).filter((row) => row.category === cat.id);

              return (
                <div key={cat.id} className="rounded-2xl border border-[#3e2723]/10 bg-[#faf8f8]/60 p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="font-display text-base font-bold text-[#3e2723]">
                        {cat.name}
                        {required ? (
                          <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-[#3e2723]/45">
                            {c.always_on}
                          </span>
                        ) : null}
                      </h3>
                      <p className="mt-1 text-sm text-[#5c4039]">{cat.description}</p>
                    </div>
                    {!required ? (
                      <label className="relative inline-flex shrink-0 cursor-pointer items-center">
                        <input
                          type="checkbox"
                          className="peer sr-only"
                          checked={checked}
                          onChange={(e) =>
                            setDraftCategories((prev) => ({ ...prev, [cat.id]: e.target.checked }))
                          }
                        />
                        <span className="h-7 w-12 rounded-full bg-[#3e2723]/15 transition peer-checked:bg-[#3e2723]" />
                        <span className="absolute left-0.5 top-0.5 h-6 w-6 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
                      </label>
                    ) : null}
                  </div>
                  <div className="mt-4">
                    <CookieTable cookies={cookies} labels={tableLabels} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex flex-col gap-2 border-t border-[#3e2723]/10 pt-5 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={denyAll}
              className="rounded-full border border-[#3e2723]/15 px-5 py-2.5 text-sm font-semibold text-[#3e2723] hover:bg-[#faf8f8]"
            >
              {c.reject_all}
            </button>
            <button
              type="button"
              onClick={() => savePreferences(draftCategories)}
              className="rounded-full bg-[#3e2723] px-5 py-2.5 text-sm font-semibold text-[#fbd9e5] hover:bg-[#2e1e1b]"
            >
              {c.save_preferences}
            </button>
            <button
              type="button"
              onClick={acceptAll}
              className="rounded-full border border-[#3e2723] px-5 py-2.5 text-sm font-semibold text-[#3e2723] hover:bg-[#faf8f8]"
            >
              {c.accept_all}
            </button>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CookieDeclarationPanel() {
  const { t } = useLanguage();
  const c = t.cookieConsent;
  const decl = t.cookieDeclaration;
  const { consent, openSettings, withdrawConsent } = useCookieConsent();

  const domain = typeof window !== "undefined" ? window.location.hostname : "socialdestiny.ro";
  const consentDate = consent?.date
    ? new Date(consent.date).toLocaleString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      })
    : c.not_set;

  const tableLabels = {
    col_name: c.col_name,
    col_provider: c.col_provider,
    col_purpose: c.col_purpose,
    col_duration: c.col_duration,
    col_type: c.col_type,
    no_cookies: c.no_cookies,
  };

  return (
    <section className="mt-12 rounded-2xl border border-[#3e2723]/10 bg-[#faf8f8] p-6 sm:p-8" data-testid="cookie-declaration">
      <h2 className="font-display text-2xl font-bold text-[#3e2723]">{decl.title}</h2>
      <p className="mt-4 text-sm leading-relaxed text-[#5c4039]">{decl.intro}</p>
      <p className="mt-3 text-sm leading-relaxed text-[#5c4039]">{decl.consent_note}</p>

      <div className="mt-6 space-y-2 rounded-xl border border-[#3e2723]/10 bg-white p-4 text-sm text-[#5c4039]">
        <p>
          <span className="font-semibold text-[#3e2723]">{c.domain_label}:</span> {domain}
        </p>
        <p>
          <span className="font-semibold text-[#3e2723]">{c.state_label}:</span>{" "}
          {consent ? consentStateLabel(consent.state, t) : c.not_set}
        </p>
        {consent ? (
          <>
            <p>
              <span className="font-semibold text-[#3e2723]">{c.consent_id_label}:</span>{" "}
              <code className="break-all rounded bg-[#faf8f8] px-1.5 py-0.5 text-xs">{consent.id}</code>
            </p>
            <p>
              <span className="font-semibold text-[#3e2723]">{c.consent_date_label}:</span> {consentDate}
            </p>
          </>
        ) : null}
        <div className="flex flex-wrap gap-3 pt-2">
          <button
            type="button"
            onClick={openSettings}
            className="text-sm font-semibold text-[#3e2723] underline underline-offset-2"
          >
            {c.change_consent}
          </button>
          <span className="text-[#3e2723]/25">|</span>
          <button
            type="button"
            onClick={withdrawConsent}
            className="text-sm font-semibold text-[#3e2723] underline underline-offset-2"
          >
            {c.withdraw_consent}
          </button>
        </div>
      </div>

      <p className="mt-6 text-xs text-[#3e2723]/55">
        {decl.last_updated_label}: {decl.last_updated}
      </p>

      <div className="mt-8 space-y-8">
        {(decl.categories || []).map((cat) => {
          const cookies = (decl.cookies || []).filter((row) => row.category === cat.id);
          return (
            <div key={cat.id}>
              <h3 className="font-display text-lg font-bold text-[#3e2723]">
                {cat.name} ({cookies.length})
              </h3>
              <p className="mt-2 text-sm text-[#5c4039]">{cat.description}</p>
              <div className="mt-4">
                <CookieTable cookies={cookies} labels={tableLabels} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
